import { Button } from '@mui/material';
import cn from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DownloadIcon from '../../assets/images/icon-download-inversed.svg';
import { AsyncButton, BoxItem, NoResultsPlaceholder, PaginatedList, TextIconButton } from '../../components';
import { AddButton } from '../../components/forms';
import { IPaginatedListChangeEvent, IPaginatedListRef } from '../../components/PaginatedList/PaginatedList';
import {
  CAMPAIGN_LABEL,
  DEPARTMENT_IDS,
  INITIAL_CAMPAIGN_FORM_STATE,
  MAX_CAMPAIGN_ITEMS_PER_PAGE,
} from '../../constants/campaigns';
import { FULFILLMENT_CENTER_ID } from '../../constants/inventories';
import { routes, URL_VARS } from '../../constants/routing';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../constants/users';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import useDeptFilter from '../../hooks/useDeptFilter';
import useFulfillmentCenterFilter from '../../hooks/useFulfillmentCentersFilter';
import useSearchFilter from '../../hooks/useSearchFilter/useSearchFilter';
import useWindowSize from '../../hooks/useWindowSize';
import {
  addCampaignValue,
  downloadCampaignReportCSVRequest,
  fetchCampaignsRequest,
} from '../../store/actions/campaigns';
import { fetchFulfillmentCentersRequest } from '../../store/actions/fulfillmentCenters';
import { toggleDownloadSuccessModal } from '../../store/actions/modals';
import { selectAdminType } from '../../store/selectors/auth';
import {
  selectCampaignsByType,
  selectCampaignsCount,
  selectCanEditCampaigns,
  selectIsCampaignReportLoading,
  selectIsFinalCampaign,
} from '../../store/selectors/campaigns';
import { ICampaign, IFetchCampaignsRequest } from '../../types/campaigns';
import { IDeleteModalProps } from '../../types/modals';
import { IPromiseCallbacks } from '../../types/redux';
import { IApiError, IDownloadCSVSuccessPayload, NotificationListEnum } from '../../types/shell';
import notification from '../../utils/notification';
import { hasPermission } from '../../utils/users';
import BoxDetailsSidebar from '../BoxDetailsSidebar/BoxDetailsSidebar';

import styles from './CampaignsContainer.module.scss';

interface IProps {
  openDeleteModal?: (prop: IDeleteModalProps) => any;
}

const CampaignsContainer = ({ openDeleteModal }: IProps) => {
  const { mobile } = useWindowSize();
  const history = useHistory();
  const dispatch = useDispatch();
  const { setHeader, setAdditionalData } = useHeaderDispatch();

  const canEditCampaigns = useSelector(selectCanEditCampaigns);
  const campaignItems = useSelector(selectCampaignsByType());
  const campaignsCount = useSelector(selectCampaignsCount);
  const isFinalCampaign = useSelector(selectIsFinalCampaign);
  const adminType = useSelector(selectAdminType);
  const isCampaignReportLoading = useSelector(selectIsCampaignReportLoading);

  const [selectedBox, setSelectedBox] = useState<ICampaign | undefined>();

  const paginatedListRef = useRef<IPaginatedListRef<ICampaign>>(null);
  const campaignListRef = useRef<HTMLDivElement>(null);

  const { SearchInput, query } = useSearchFilter({
    items: [],
    fieldNames: [],
    props: {
      className: styles.searchByFilterContainer,
      hint: 'Look up specific campaigns by their name.',
      dataAction: 'campaign_search_input',
    },
  });

  const {
    departments: selectedDepartment,
    DeptFilter,
    filteredItems,
  } = useDeptFilter<ICampaign>((campaignItems as ICampaign[]) || [], DEPARTMENT_IDS, {
    className: styles.dropdownFilter,
    contentClassName: styles.dropdownContent,
    labelClassName: styles.dropdownLabel,
    dataAction: 'campaign_department_filter',
  });

  const {
    FulfillmentCenterFilter,
    filteredItems: filteredItemsByFC,
    fulfillmentCenters: selectedFulfillmentCenter,
  } = useFulfillmentCenterFilter<ICampaign>(filteredItems || [], FULFILLMENT_CENTER_ID, {
    className: styles.dropdownFilter,
    contentClassName: styles.dropdownContent,
    labelClassName: styles.dropdownLabel,
    dataAction: 'campaign_fulfillment_center_filter',
  });

  const getRequestInfo = useCallback(
    (payload: IFetchCampaignsRequest) => {
      return {
        ...(selectedDepartment && { department_id: selectedDepartment as string }),
        ...(query && { search_query: query }),
        ...(selectedFulfillmentCenter && { fulfillment_center_id: selectedFulfillmentCenter as string }),
        ...payload,
      };
    },
    [selectedDepartment, selectedFulfillmentCenter, query],
  );

  const fetchCampaigns = useCallback(
    (payload: IFetchCampaignsRequest) => {
      dispatch(fetchCampaignsRequest(getRequestInfo(payload)));
    },
    [dispatch, getRequestInfo],
  );

  const handlePaginationChange = useCallback(
    ({ page, setPage }: IPaginatedListChangeEvent) => {
      fetchCampaigns({ page, page_size: MAX_CAMPAIGN_ITEMS_PER_PAGE });
      // In case there's an error on the endpoint, paginator will change the page number, which is not correct.
      // The fix is to wrap the request with the Promise and call the `setPage(page)` after the Promise is resolved
      setPage(page);
    },
    [fetchCampaigns],
  );

  const handleCreateCampaign = useCallback(() => {
    dispatch(addCampaignValue(INITIAL_CAMPAIGN_FORM_STATE));
    history.push(
      routes.campaigns.getCampaignUrl({
        flowId: URL_VARS.NEW,
        stepId: URL_VARS.DETAILS,
      }),
    );
  }, [dispatch, history]);

  const handleEditCampaign = useCallback(
    (box: ICampaign) => {
      dispatch(addCampaignValue(box));
      history.push(
        routes.campaigns.getCampaignUrl({
          flowId: URL_VARS.EDIT,
          stepId: URL_VARS.DETAILS,
          itemId: box.box_id,
        }),
      );
    },
    [dispatch, history],
  );

  const handleCloseSidebar = useCallback(() => {
    setSelectedBox(undefined);
  }, []);

  const handleDeleteCampaign = useCallback(
    (id: string) => {
      if (openDeleteModal && id) {
        openDeleteModal({
          id,
          cb: () => {
            handleCloseSidebar();
          },
        });
      }
    },
    [openDeleteModal, handleCloseSidebar],
  );

  const handleBoxClick = useCallback((box: ICampaign) => {
    setSelectedBox(box);
  }, []);

  const downloadCampaignCSV = useCallback(
    (payload: IPromiseCallbacks) => {
      dispatch(downloadCampaignReportCSVRequest(payload));
    },
    [dispatch],
  );

  const handleDownloadClick = useCallback(() => {
    const downloadCampaignCSVPromise = new Promise<IDownloadCSVSuccessPayload>((resolve, reject) => {
      downloadCampaignCSV({ resolve, reject });
    }).then((response: IDownloadCSVSuccessPayload) => {
      dispatch(toggleDownloadSuccessModal(response));
    });

    notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
      promise: downloadCampaignCSVPromise,
      promiseParams: {
        pending: 'Downloading process started. We’ll notify you when the file is ready!',
        error: {
          render: ({ data: error }) =>
            (error && error.message) || "Something bad has happened. CSV file wasn't created",
        },
      },
    });
  }, [downloadCampaignCSV, dispatch]);

  const actionButtons = useMemo(
    () => [
      <Button
        key="editButton"
        variant="contained"
        onClick={() => {
          if (selectedBox) {
            handleEditCampaign(selectedBox);
            handleCloseSidebar();
          }
        }}
      >
        Edit campaign
      </Button>,
      <Button
        key="deleteButton"
        variant="contained"
        color="error"
        onClick={() => {
          if (selectedBox) {
            handleDeleteCampaign(selectedBox.box_id);
          }
        }}
      >
        Delete campaign
      </Button>,
    ],
    [handleEditCampaign, handleDeleteCampaign, selectedBox, handleCloseSidebar],
  );

  const getBoxItem = useCallback(
    (box: ICampaign) => {
      return (
        <div key={box.box_id} className={styles.item}>
          <BoxItem
            key={box.box_id}
            box={box}
            withWarning
            className={cn(styles.itemWrapper, styles.wide)}
            iconClassName={styles.icon}
            onClick={() => handleBoxClick(box)}
            {...(canEditCampaigns
              ? {
                  onDelete: () => handleDeleteCampaign(box.box_id),
                  onEdit: () => handleEditCampaign(box),
                }
              : null)}
          />
        </div>
      );
    },
    [canEditCampaigns, handleBoxClick, handleDeleteCampaign, handleEditCampaign],
  );

  const renderControls = useMemo(() => {
    if (!canEditCampaigns) {
      return null;
    }

    return (
      <div className={styles.controls}>
        {mobile ? (
          <>
            {isCampaignReportLoading ? (
              <AsyncButton
                loaderClassName={styles.spinner}
                disabled
                outlined
                className={styles.controlButton}
                isLoading
              />
            ) : (
              <TextIconButton icon={DownloadIcon} onClick={handleDownloadClick} trigger />
            )}
          </>
        ) : (
          <AsyncButton
            disabled={isCampaignReportLoading}
            onClick={handleDownloadClick}
            className={cn(styles.controlButton, styles.loaderBtn)}
            loaderClassName={styles.spinner}
            isLoading={isCampaignReportLoading}
            outlined
            dataAction="campaign_download_report_button"
          >
            Download report
          </AsyncButton>
        )}
        <AddButton
          assetName={CAMPAIGN_LABEL}
          className={styles.controlButton}
          onClick={handleCreateCampaign}
          dataAction="campaign_add_new_button"
        />
      </div>
    );
  }, [mobile, canEditCampaigns, isCampaignReportLoading, handleDownloadClick, handleCreateCampaign]);

  useLayoutEffect(() => {
    fetchCampaigns({ page: 1, page_size: MAX_CAMPAIGN_ITEMS_PER_PAGE });

    paginatedListRef.current?.resetPageCount();
  }, [fetchCampaigns]);

  useEffect(() => {
    if (!setAdditionalData) {
      return;
    }

    setAdditionalData(mobile ? renderControls : null);

    // Need to clean additional information after yourself
    return setAdditionalData;
  }, [mobile, renderControls, setAdditionalData]);

  useEffect(() => {
    if (setHeader) {
      setHeader({ title: 'Campaigns list', action: () => history.push(routes.dashboard) });
    }
  }, [history, setHeader]);

  useEffect(() => {
    dispatch(fetchFulfillmentCentersRequest({ hasOrgInventory: true }));
  }, [dispatch]);

  return (
    <div className={cn(styles.container)}>
      <div className={styles.header}>
        <div className={styles.filter}>
          {SearchInput}
          <div className={styles.commonFilters}>
            {hasPermission([ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN], adminType) ? DeptFilter({}) : null}
            {FulfillmentCenterFilter({ isFiltered: true })}
          </div>
        </div>
        {!mobile && renderControls}
      </div>
      {filteredItems && filteredItems.length ? (
        <PaginatedList<ICampaign>
          totalCount={campaignsCount}
          ref={paginatedListRef}
          items={filteredItemsByFC || ([] as ICampaign[])}
          pageSize={MAX_CAMPAIGN_ITEMS_PER_PAGE}
          onChange={handlePaginationChange}
          isFinalItems={isFinalCampaign}
          scrollContainerRef={campaignListRef}
        >
          {({ items, pagination: Pagination }) => (
            <>
              <div className={styles.list} ref={campaignListRef}>
                {items?.map(getBoxItem)}
              </div>
              <Pagination className={styles.pagination} />
            </>
          )}
        </PaginatedList>
      ) : (
        <NoResultsPlaceholder label="No campaigns yet" className={styles.noItems} labelClassName={styles.label} />
      )}
      <BoxDetailsSidebar
        onClose={handleCloseSidebar}
        box={selectedBox}
        actionButtons={canEditCampaigns ? actionButtons : undefined}
      />
    </div>
  );
};

export default CampaignsContainer;
